(function () {
  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const money = (value) => {
    const number = Number(value || 0);
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number.isFinite(number) ? number : 0);
  };

  const parseJSON = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const initHeader = () => {
    const header = document.querySelector('[data-morphe-header]');
    if (!header) return;

    const menuButton = header.querySelector('[data-morphe-menu-button]');
    const menu = header.querySelector('[data-morphe-menu]');

    const syncHeader = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 20);
    };

    syncHeader();
    window.addEventListener('scroll', syncHeader, { passive: true });

    menuButton?.addEventListener('click', () => {
      const open = !menu.classList.contains('is-open');
      menu.classList.toggle('is-open', open);
      menuButton.classList.toggle('is-open', open);
      menuButton.setAttribute('aria-expanded', String(open));
    });

    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const activeShopMenu = document.activeElement?.closest?.('[data-morphe-shop-menu]');
      if (activeShopMenu) document.activeElement?.blur();
    });
  };

  const initHero = () => {
    const hero = document.querySelector('[data-morphe-hero]');
    if (!hero) return;

    const subtitle = hero.querySelector('[data-morphe-hero-subtitle]');
    const text = hero.querySelector('[data-morphe-hero-copy]');
    const countdown = hero.querySelector('[data-morphe-countdown]');
    const soundToggle = hero.querySelector('[data-morphe-hero-sound]');
    const soundStatus = hero.querySelector('[data-morphe-hero-sound-status]');
    const audio = hero.querySelector('audio[data-morphe-hero-audio]');
    const releaseAt = countdown ? new Date(countdown.getAttribute('data-release-at') || '').getTime() : 0;

    let soundEnabled = false;
    let countdownTimer = null;
    const initialCountdown = {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
      isLive: false,
    };

    const formatCountdown = () => {
      if (!countdown) return;
      const remaining = Math.max(0, releaseAt - Date.now());
      if (!releaseAt || remaining === 0) {
        countdown.textContent = 'now live';
        return;
      }

      const data = {
        days: String(Math.floor(remaining / 86400000)).padStart(2, '0'),
        hours: String(Math.floor((remaining / 3600000) % 24)).padStart(2, '0'),
        minutes: String(Math.floor((remaining / 60000) % 60)).padStart(2, '0'),
        seconds: String(Math.floor((remaining / 1000) % 60)).padStart(2, '0'),
      };

      countdown.innerHTML = `releasing july 6 · 4 pm ist<br><strong>${data.days}d ${data.hours}h ${data.minutes}m ${data.seconds}s</strong>`;
    };

    const update = () => {
      const progress = clamp(window.scrollY / 800, 0, 1);
      if (subtitle) {
        subtitle.style.opacity = String(clamp((progress - 0.02) * 9, 0, 1));
      }
      if (text) {
        text.style.opacity = String(1 - Math.min(progress * 1.5, 1));
      }
      if (audio) {
        if (!soundEnabled) {
          audio.pause();
          return;
        }

        const nextVolume = Math.max(0, (1 - progress) * 0.35);
        audio.volume = nextVolume;
        audio.muted = false;
        if (nextVolume > 0 && audio.paused) {
          audio.play().catch(() => {});
        }
        if (nextVolume <= 0 && !audio.paused) {
          audio.pause();
        }
      }
    };

    soundToggle?.addEventListener('click', async () => {
      const label = soundToggle.querySelector('[data-morphe-hero-sound-label]');
      if (!audio) return;

      if (soundEnabled) {
        soundEnabled = false;
        audio.pause();
        soundToggle.setAttribute('aria-pressed', 'false');
        if (label) label.textContent = 'play sound';
        if (soundStatus) soundStatus.textContent = 'sound paused';
        return;
      }

      const progress = clamp(window.scrollY / 800, 0, 1);
      audio.volume = Math.max(0, (1 - progress) * 0.35);
      audio.muted = false;
      try {
        await audio.play();
        soundEnabled = true;
        soundToggle.setAttribute('aria-pressed', 'true');
        if (label) label.textContent = 'sound on';
        if (soundStatus) soundStatus.textContent = 'sound playing';
        update();
      } catch {
        soundEnabled = false;
        soundToggle.setAttribute('aria-pressed', 'false');
        if (label) label.textContent = 'play sound';
        if (soundStatus) soundStatus.textContent = 'sound unavailable';
      }
    });

    formatCountdown();
    if (countdown && releaseAt && Date.now() < releaseAt) {
      countdownTimer = window.setInterval(formatCountdown, 1000);
    }

    update();
    window.addEventListener('scroll', update, { passive: true });

    if (countdownTimer) {
      window.addEventListener('beforeunload', () => window.clearInterval(countdownTimer), { once: true });
    }
  };

  const initCarousel = () => {
    document.querySelectorAll('[data-morphe-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-morphe-carousel-track]');
      if (!track) return;

      const prev = carousel.querySelector('[data-morphe-carousel-prev]');
      const next = carousel.querySelector('[data-morphe-carousel-next]');

      const scrollByCard = (direction) => {
        const slide = track.querySelector('[data-morphe-carousel-item]');
        const width = slide ? slide.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: direction * width, behavior: 'smooth' });
      };

      prev?.addEventListener('click', () => scrollByCard(-1));
      next?.addEventListener('click', () => scrollByCard(1));
    });
  };

  const initProductCards = (root = document) => {
    root.querySelectorAll('[data-morphe-card]').forEach((card) => {
      if (card.dataset.morpheCardReady === 'true') return;
      card.dataset.morpheCardReady = 'true';

      const track = card.querySelector('[data-morphe-card-track]');
      const slides = Array.from(card.querySelectorAll('[data-morphe-card-slide]'));
      const colorButtons = Array.from(card.querySelectorAll('[data-morphe-card-color-button]'));
      const controls = card.querySelector('[data-morphe-card-controls]');
      const dots = card.querySelector('[data-morphe-card-dots]');
      const previous = card.querySelector('[data-morphe-card-prev]');
      const next = card.querySelector('[data-morphe-card-next]');
      if (!track || !slides.length) return;

      let activeColor = colorButtons[0]?.getAttribute('data-morphe-card-color-value') || '';
      let visibleSlides = [];
      let activeIndex = 0;
      let wheelLocked = false;
      let dragState = null;
      let suppressCardClick = false;

      const colorMatches = (slide, color) => (
        String(slide.getAttribute('data-morphe-card-color') || '').toLowerCase() === String(color || '').toLowerCase()
      );

      const positionSlides = (behavior = 'smooth', dragOffset = 0) => {
        track.style.transition = behavior === 'smooth'
          ? 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)'
          : 'none';
        track.style.transform = `translate3d(calc(${activeIndex * -100}% + ${dragOffset}px), 0, 0)`;
      };

      const updateActiveSlide = (index, behavior = 'smooth') => {
        if (!visibleSlides.length) return;
        activeIndex = (index + visibleSlides.length) % visibleSlides.length;
        positionSlides(behavior);
        dots?.querySelectorAll('button').forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === activeIndex);
          dot.setAttribute('aria-current', String(dotIndex === activeIndex));
        });
      };

      const renderDots = () => {
        if (!dots) return;
        dots.replaceChildren(...visibleSlides.map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = index === activeIndex ? 'morphe-product-card__gallery-dot is-active' : 'morphe-product-card__gallery-dot';
          dot.setAttribute('aria-label', `view image ${index + 1}`);
          dot.setAttribute('aria-current', String(index === activeIndex));
          dot.addEventListener('click', () => updateActiveSlide(index));
          return dot;
        }));
      };

      const selectColor = (color) => {
        activeColor = color;
        const colorSlides = slides.filter((slide) => colorMatches(slide, color));
        visibleSlides = colorSlides.length ? colorSlides : slides;
        slides.forEach((slide) => {
          slide.hidden = !visibleSlides.includes(slide);
        });
        colorButtons.forEach((button) => {
          const selected = button.getAttribute('data-morphe-card-color-value') === color;
          button.classList.toggle('is-active', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        activeIndex = 0;
        renderDots();
        if (controls) controls.hidden = visibleSlides.length <= 1;
        updateActiveSlide(0, 'auto');
      };

      colorButtons.forEach((button) => {
        button.addEventListener('click', () => selectColor(button.getAttribute('data-morphe-card-color-value') || ''));
      });
      previous?.addEventListener('click', () => updateActiveSlide(activeIndex - 1));
      next?.addEventListener('click', () => updateActiveSlide(activeIndex + 1));
      track.addEventListener('wheel', (event) => {
        const horizontalIntent = event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (!horizontalIntent) return;

        const delta = event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY)
          ? event.deltaY
          : event.deltaX;
        if (!delta || wheelLocked) return;
        event.preventDefault();
        wheelLocked = true;
        updateActiveSlide(activeIndex + (delta > 0 ? 1 : -1));
        window.setTimeout(() => { wheelLocked = false; }, 520);
      }, { passive: false });
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragState = { id: event.pointerId, startX: event.clientX, moved: false, distance: 0 };
        track.setPointerCapture?.(event.pointerId);
      });
      track.addEventListener('pointermove', (event) => {
        if (!dragState || dragState.id !== event.pointerId) return;
        const distance = event.clientX - dragState.startX;
        if (Math.abs(distance) < 4 && !dragState.moved) return;

        event.preventDefault();
        dragState.moved = true;
        dragState.distance = distance;
        track.classList.add('is-dragging');
        positionSlides('auto', distance);
      });
      const endDrag = (event) => {
        if (!dragState || dragState.id !== event.pointerId) return;
        const didMove = dragState.moved;
        const distance = dragState.distance;
        dragState = null;
        track.classList.remove('is-dragging');
        if (!didMove) return;

        suppressCardClick = true;
        window.setTimeout(() => { suppressCardClick = false; }, 250);
        const threshold = track.clientWidth * 0.15;
        if (distance <= -threshold) {
          updateActiveSlide(activeIndex + 1);
        } else if (distance >= threshold) {
          updateActiveSlide(activeIndex - 1);
        } else {
          updateActiveSlide(activeIndex);
        }
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('click', (event) => {
        if (!suppressCardClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      selectColor(activeColor);
    });
  };

  const initCollection = () => {
    const root = document.querySelector('[data-morphe-collection]');
    if (!root) return;

    const cards = Array.from(root.querySelectorAll('[data-morphe-product-card]'));
    const grid = root.querySelector('[data-morphe-collection-grid]');
    const count = root.querySelector('[data-morphe-collection-count]');
    const typeButtons = Array.from(root.querySelectorAll('[data-morphe-filter-type]'));
    const colorButtons = Array.from(root.querySelectorAll('[data-morphe-filter-color]'));
    const sortSelect = root.querySelector('[data-morphe-sort]');
    const clearButton = root.querySelector('[data-morphe-clear-filters]');
    const sortToggle = root.querySelector('[data-morphe-sort-toggle]');
    const filterToggle = root.querySelector('[data-morphe-filter-toggle]');
    const sortPanel = root.querySelector('[data-morphe-sort-panel]');
    const filterPanel = root.querySelector('[data-morphe-filter-panel]');
    const colorPanelButton = root.querySelector('[data-morphe-color-panel-button]');
    const colorPanel = root.querySelector('[data-morphe-color-panel]');

    const state = {
      types: new Set(),
      colors: new Set(),
      sort: 'all',
    };

    initProductCards(root);

    const cardData = new Map(cards.map((card, index) => [card, {
      index,
      productType: card.getAttribute('data-product-type') || '',
      productColors: (card.getAttribute('data-product-colors') || '').split('|').filter(Boolean).map((value) => value.toLowerCase()),
      price: Number(card.getAttribute('data-product-price') || 0),
      title: (card.getAttribute('data-product-title') || '').toLowerCase(),
    }]));

    const apply = () => {
      let visible = cards.filter((card) => {
        const data = cardData.get(card);

        if (state.types.size && !state.types.has(data.productType)) return false;
        if (state.colors.size && !data.productColors.some((color) => state.colors.has(color))) return false;
        return true;
      });

      visible.sort((a, b) => {
        const dataA = cardData.get(a);
        const dataB = cardData.get(b);

        switch (state.sort) {
          case 'price-asc':
            return dataA.price - dataB.price;
          case 'price-desc':
            return dataB.price - dataA.price;
          case 'title-asc':
            return dataA.title.localeCompare(dataB.title);
          default:
            return dataA.index - dataB.index;
        }
      });

      const visibleCards = new Set(visible);
      cards.forEach((card) => {
        card.hidden = !visibleCards.has(card);
      });

      if (grid) {
        const hiddenCards = cards.filter((card) => !visibleCards.has(card));
        grid.append(...visible, ...hiddenCards);
      }

      if (count) {
        count.textContent = `${visible.length} ${visible.length === 1 ? 'piece' : 'pieces'}`;
      }
    };

    typeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.getAttribute('data-filter-value')?.toLowerCase();
        if (!value) return;
        if (state.types.has(value)) {
          state.types.delete(value);
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        } else {
          state.types.add(value);
          button.classList.add('is-active');
          button.setAttribute('aria-pressed', 'true');
        }
        apply();
      });
    });

    colorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.getAttribute('data-filter-value')?.toLowerCase();
        if (!value) return;
        if (state.colors.has(value)) {
          state.colors.delete(value);
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        } else {
          state.colors.add(value);
          button.classList.add('is-active');
          button.setAttribute('aria-pressed', 'true');
        }
        apply();
      });
    });

    sortSelect?.addEventListener('change', () => {
      state.sort = sortSelect.value || 'all';
      apply();
    });

    clearButton?.addEventListener('click', () => {
      state.types.clear();
      state.colors.clear();
      state.sort = 'all';
      typeButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      colorButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      if (sortSelect) sortSelect.value = 'all';
      apply();
    });

    sortToggle?.addEventListener('click', () => {
      if (sortPanel) sortPanel.hidden = !sortPanel.hidden;
    });

    filterToggle?.addEventListener('click', () => {
      if (filterPanel) filterPanel.hidden = !filterPanel.hidden;
    });

    colorPanelButton?.addEventListener('click', () => {
      const isOpen = colorPanel?.hidden === false;
      if (colorPanel) colorPanel.hidden = isOpen;
    });

    apply();
  };

  const initProduct = () => {
    const root = document.querySelector('[data-morphe-product]');
    if (!root) return;

    const state = parseJSON(root.querySelector('[data-morphe-product-json]')?.textContent || '{}', {});
    const variants = Array.isArray(state.variants) ? state.variants : [];
    const sizeTemplates = {
      DR: [
        { key: 'chest', label: 'Chest' },
        { key: 'waist', label: 'Waist' },
        { key: 'hip', label: 'Hip' },
        { key: 'dress_length', label: 'Dress Length' },
        { key: 'long_dress_length', label: 'Long Dress Length' },
      ],
      PA: [
        { key: 'waist', label: 'Waist' },
        { key: 'hip', label: 'Hip' },
        { key: 'length', label: 'Length' },
      ],
    };
    const template = sizeTemplates[String(state.sizeCategory || '').toUpperCase()] || [];
    const gallery = root.querySelector('[data-morphe-product-gallery]');
    const desktopGallery = root.querySelector('[data-morphe-desktop-gallery]');
    const thumbs = root.querySelector('[data-morphe-product-thumbs]');
    const variantInput = root.querySelector('[name="id"]');
    const priceNode = root.querySelector('[data-morphe-product-price]');
    const titleNode = root.querySelector('[data-morphe-product-title]');
    const sizeButtons = Array.from(root.querySelectorAll('[data-morphe-size-button]'));
    const colorButtons = Array.from(root.querySelectorAll('[data-morphe-color-button]'));
    const versionButtons = Array.from(root.querySelectorAll('[data-morphe-version-button]'));
    const addButton = root.querySelector('[data-morphe-add-to-cart]');
    const addButtonLabel = root.querySelector('[data-morphe-add-label]');
    const addWrap = root.querySelector('[data-morphe-add-wrap]');
    const customPanel = root.querySelector('[data-morphe-custom-size-panel]');
    const customInputs = Array.from(root.querySelectorAll('[data-morphe-custom-input]'));
    const errorNode = root.querySelector('[data-morphe-custom-error]');
    const sizeGuide = root.querySelector('[data-morphe-size-guide]');
    const sizeGuideToggle = root.querySelector('[data-morphe-size-guide-toggle]');
    const sizeGuideClose = root.querySelector('[data-morphe-size-guide-close]');
    const accordion = Array.from(root.querySelectorAll('[data-morphe-accordion]'));
    const modal = root.querySelector('[data-morphe-lightbox]');
    const modalImage = root.querySelector('[data-morphe-lightbox-image]');
    const modalIndex = root.querySelector('[data-morphe-lightbox-index]');
    const modalPrev = root.querySelector('[data-morphe-lightbox-prev]');
    const modalNext = root.querySelector('[data-morphe-lightbox-next]');
    const modalThumbs = root.querySelector('[data-morphe-lightbox-thumbs]');
    const modalClose = root.querySelector('[data-morphe-lightbox-close]');
    const track = root.querySelector('[data-morphe-mobile-track]');
    let trackDots = Array.from(root.querySelectorAll('[data-morphe-mobile-dot]'));

    let activeVariant = variants.find((variant) => variant.available !== false) || variants[0] || null;
    let selectedColor = activeVariant?.color || '';
    let selectedVersion = activeVariant?.version || '';
    let selectedSize = null;
    let activeImages = [];
    let lightboxIndex = 0;

    const inferImages = (variant) => {
      const images = [];
      if (variant?.featured_image?.src) images.push(variant.featured_image.src);
      if (Array.isArray(variant?.images)) images.push(...variant.images);
      return [...new Set(images.filter(Boolean))];
    };

    const buildImage = (src, index, className) => {
      const image = document.createElement('img');
      image.src = src;
      image.alt = `${state.title || 'product'} image ${index + 1}`;
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.className = className;
      return image;
    };

    const renderGallery = () => {
      if (gallery) {
        const mobileSlides = activeImages.map((src, index) => {
          const slide = document.createElement('button');
          slide.type = 'button';
          slide.className = 'morphe-track__slide';
          slide.dataset.imageIndex = String(index);
          slide.setAttribute('aria-label', `Open ${state.title || 'product'} image ${index + 1}`);
          slide.appendChild(buildImage(src, index, 'morphe-product__mobile-image'));
          slide.addEventListener('click', () => openLightbox(index));
          return slide;
        });
        gallery.replaceChildren(...mobileSlides);
        gallery.scrollLeft = 0;
      }

      if (desktopGallery) {
        const desktopSlides = activeImages.map((src, index) => {
          const slide = document.createElement('button');
          slide.type = 'button';
          slide.className = 'morphe-product__desktop-slide';
          slide.setAttribute('aria-label', `Open ${state.title || 'product'} image ${index + 1}`);
          slide.appendChild(buildImage(src, index, 'morphe-product__desktop-image'));
          slide.addEventListener('click', () => openLightbox(index));
          return slide;
        });
        desktopGallery.replaceChildren(...desktopSlides);
      }

      if (thumbs) {
        const thumbButtons = activeImages.map((_, index) => {
          const thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = index === 0 ? 'morphe-product__mobile-thumb is-active' : 'morphe-product__mobile-thumb';
          thumb.textContent = String(index + 1);
          thumb.addEventListener('click', () => {
            gallery?.scrollTo({ left: index * gallery.clientWidth, behavior: 'smooth' });
          });
          return thumb;
        });
        thumbs.replaceChildren(...thumbButtons);
        trackDots = thumbButtons;
      }
    };

    const syncPrice = () => {
      if (!priceNode) return;
      const price = activeVariant?.price ?? state.price ?? 0;
      priceNode.textContent = `₹${money(price)}`;
    };

    const syncVariant = () => {
      if (variantInput && activeVariant?.id) {
        variantInput.value = activeVariant.id;
      }

      const variantImages = inferImages(activeVariant);
      const productImages = (Array.isArray(state.productImages) ? state.productImages : [])
        .map((image) => (typeof image === 'string' ? { src: image, color: '' } : image))
        .filter((image) => image?.src);
      const colorImages = productImages.filter((image) => sameValue(image.color, selectedColor));
      const unassignedImages = productImages.filter((image) => !image.color);
      const galleryImages = colorImages.length ? colorImages : unassignedImages;
      activeImages = [...new Set([...variantImages, ...galleryImages.map((image) => image.src)].filter(Boolean))];

      syncPrice();
      renderGallery();
    };

    const sameValue = (left, right) => String(left || '').toLowerCase() === String(right || '').toLowerCase();

    const matchingVariants = ({ color = selectedColor, version = selectedVersion, size } = {}) => (
      variants.filter((variant) => (
        sameValue(variant.color, color)
        && sameValue(variant.version, version)
        && (size === undefined || sameValue(variant.size, size))
      ))
    );

    const firstAvailable = (items) => items.find((variant) => variant.available !== false) || items[0] || null;

    const clearCustomMeasurements = () => {
      errorNode && (errorNode.textContent = '');
      customInputs.forEach((input) => {
        input.value = '';
      });
    };

    const updateColorButtons = () => {
      colorButtons.forEach((button) => {
        const value = button.getAttribute('data-color-value');
        const active = sameValue(value, selectedColor);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const updateVersionButtons = () => {
      versionButtons.forEach((button) => {
        const value = button.getAttribute('data-version-value');
        const visible = variants.some((variant) => sameValue(variant.color, selectedColor) && sameValue(variant.version, value));
        const active = sameValue(value, selectedVersion);
        button.hidden = !visible;
        button.disabled = !visible;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const updateSizeOptions = () => {
      sizeButtons.forEach((button) => {
        const value = button.getAttribute('data-size-value');
        const isCustomSize = value === 'CUSTOM';
        const isFreeSize = value === 'FREE SIZE';
        const sizeVariant = firstAvailable(matchingVariants({ size: value }));
        const visible = isCustomSize || isFreeSize || Boolean(sizeVariant);
        const active = sameValue(selectedSize, value);
        button.hidden = !visible;
        button.disabled = !visible;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-disabled', String(!visible));
      });

      if (customPanel) {
        const showCustom = selectedSize === 'CUSTOM';
        customPanel.hidden = !showCustom;
      }
    };

    const refreshSelection = () => {
      activeVariant = firstAvailable(matchingVariants()) || activeVariant;
      syncVariant();
      updateColorButtons();
      updateVersionButtons();
      updateSizeOptions();
      updateAddButton();
    };

    const selectColor = (color) => {
      const colorVariants = variants.filter((variant) => sameValue(variant.color, color));
      if (!colorVariants.length) return;

      selectedColor = color;
      selectedVersion = colorVariants[0]?.version || '';
      selectedSize = null;
      clearCustomMeasurements();
      refreshSelection();
    };

    const openLightbox = (index) => {
      if (!modal || !activeImages.length) return;
      lightboxIndex = clamp(index, 0, activeImages.length - 1);
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      if (modalImage) modalImage.src = activeImages[lightboxIndex];
      if (modalIndex) modalIndex.textContent = `${lightboxIndex + 1} / ${activeImages.length}`;
      renderLightboxThumbs();
    };

    const closeLightbox = () => {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    };

    const renderLightboxThumbs = () => {
      if (!modalThumbs) return;
      modalThumbs.innerHTML = '';
      activeImages.forEach((src, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = index === lightboxIndex ? 'morphe-chip is-active' : 'morphe-chip';
        button.textContent = String(index + 1);
        button.addEventListener('click', () => {
          lightboxIndex = index;
          if (modalImage) modalImage.src = activeImages[index];
          if (modalIndex) modalIndex.textContent = `${lightboxIndex + 1} / ${activeImages.length}`;
          renderLightboxThumbs();
        });
        modalThumbs.appendChild(button);
      });
    };

    const updateAddButton = () => {
      if (!addButton) return;
      const customMissing = selectedSize === 'CUSTOM' && template.length && customInputs.some((input) => !input.value);
      const canAdd = Boolean(activeVariant && selectedSize && !customMissing);
      const sizeRequired = Boolean(activeVariant && !selectedSize);
      addButton.disabled = !canAdd;
      addWrap?.toggleAttribute('data-morphe-size-required', sizeRequired);
      if (addWrap) addWrap.tabIndex = sizeRequired ? 0 : -1;
      if (addButtonLabel) addButtonLabel.textContent = canAdd ? 'add to bag' : 'select size';
    };

    colorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.getAttribute('data-color-value');
        selectColor(value);
      });
    });

    versionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.getAttribute('data-version-value');
        if (!value) return;
        selectedVersion = value;
        selectedSize = null;
        clearCustomMeasurements();
        refreshSelection();
      });
    });

    sizeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.getAttribute('data-size-value');
        if (!value) return;
        if (value === 'CUSTOM' || value === 'FREE SIZE') {
          selectedSize = value;
          const sizeVariant = firstAvailable(matchingVariants({ size: value }));
          if (sizeVariant) activeVariant = sizeVariant;
        } else {
          const sizeVariant = firstAvailable(matchingVariants({ size: value }));
          if (!sizeVariant) return;
          activeVariant = sizeVariant;
          selectedSize = sizeVariant.size || value;
        }
        syncVariant();
        updateSizeOptions();
        updateAddButton();
      });
    });

    customInputs.forEach((input) => {
      input.addEventListener('input', () => {
        errorNode && (errorNode.textContent = '');
        updateAddButton();
      });
    });

    addButton?.addEventListener('click', async (event) => {
      event.preventDefault();
      if (!activeVariant || !selectedSize) return;

      const customMeasurements = {};
      customInputs.forEach((input) => {
        const key = input.getAttribute('data-field-key');
        if (key) customMeasurements[key] = input.value;
      });

      const missingCustom = selectedSize === 'CUSTOM' && template.some((field) => !customMeasurements[field.key]);
      if (missingCustom) {
        if (errorNode) errorNode.textContent = 'fill every measurement field.';
        return;
      }

      addButton.disabled = true;
      const formData = new FormData();
      formData.append('id', activeVariant.id);
      formData.append('quantity', '1');
      if (selectedSize === 'CUSTOM') {
        formData.append('properties[Size]', 'Custom size');
        formData.append('properties[Custom Measurements]', JSON.stringify(customMeasurements));
      }

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8', Accept: 'application/json' },
          body: new URLSearchParams(formData).toString(),
        });
        window.location.href = '/cart';
      } catch (error) {
        if (errorNode) errorNode.textContent = 'unable to add item to cart.';
      } finally {
        addButton.disabled = false;
      }
    });

    sizeGuideToggle?.addEventListener('click', () => {
      sizeGuide?.classList.add('is-open');
      sizeGuide?.setAttribute('aria-hidden', 'false');
    });

    sizeGuideClose?.addEventListener('click', () => {
      sizeGuide?.classList.remove('is-open');
      sizeGuide?.setAttribute('aria-hidden', 'true');
    });

    accordion.forEach((block) => {
      const trigger = block.querySelector('[data-morphe-accordion-trigger]');
      trigger?.addEventListener('click', () => {
        const open = !block.classList.contains('is-open');
        block.classList.toggle('is-open', open);
      });
    });

    modalClose?.addEventListener('click', closeLightbox);
    modalPrev?.addEventListener('click', () => openLightbox(lightboxIndex - 1));
    modalNext?.addEventListener('click', () => openLightbox(lightboxIndex + 1));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeLightbox();
        sizeGuide?.classList.remove('is-open');
      }
      if (!modal?.classList.contains('is-open')) return;
      if (event.key === 'ArrowLeft') openLightbox(lightboxIndex - 1);
      if (event.key === 'ArrowRight') openLightbox(lightboxIndex + 1);
    });

    if (track) {
      track.addEventListener('scroll', () => {
        if (!trackDots.length) return;
        const width = track.getBoundingClientRect().width;
        const index = Math.round(track.scrollLeft / width);
        trackDots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
        });
      }, { passive: true });
    }

    refreshSelection();
  };

  const initCart = () => {
    const root = document.querySelector('[data-morphe-cart]');
    if (!root) return;

    root.querySelectorAll('[data-morphe-qty-form]').forEach((form) => {
      const minus = form.querySelector('[data-morphe-qty-minus]');
      const plus = form.querySelector('[data-morphe-qty-plus]');
      const input = form.querySelector('input[name="quantity"]');
      const itemId = form.querySelector('input[name="id"]')?.value;

      const submit = async () => {
        const formData = new FormData(form);
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8', Accept: 'application/json' },
            body: new URLSearchParams(formData).toString(),
          });
          window.location.reload();
        } catch {
          window.location.reload();
        }
      };

      minus?.addEventListener('click', () => {
        input.value = String(Math.max(1, Number(input.value || 1) - 1));
        submit();
      });

      plus?.addEventListener('click', () => {
        input.value = String(Number(input.value || 1) + 1);
        submit();
      });
    });
  };

  const initCustomerAuth = () => {
    const toggle = document.querySelector('[data-morphe-recover-toggle]');
    const panel = document.querySelector('[data-morphe-recover-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) panel.querySelector('input')?.focus();
    });

    if (panel.querySelector('.morphe-auth__errors, .morphe-auth__success')) {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }
  };

  ready(() => {
    initHeader();
    initHero();
    initCarousel();
    initProductCards();
    initCollection();
    initProduct();
    initCart();
    initCustomerAuth();
  });
})();
